/**
 * Created by Ella Ma on 05/25/2019.
 *  Description:
 * ------------------
 * 2019/11/10  Yoyo Fang  servicelize line chart
 * 2019/11/11  Yoyo Fang  servicelize value tile
 * 2020/01/14  Ella Ma    Fixed bug: Click tile item, tile data is undefined
 * 2020/04/27  Ella Ma    Remove get grid data for pages, get all.
 * 2023/01/04  Simon Zhao Reused the function dashboardService.formatGridColumns to generate column definitions for the grid.
 * Updated by Daniel on 7/10/2023, added alignment property for all columns
 */
import { Component, OnInit, Input, OnDestroy, SimpleChanges, OnChanges, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Subscription, of as observableOf, Subject, Observable } from 'rxjs';
import { filter, take, catchError, delay } from 'rxjs/operators';
import { GridOptions, ColDef, CellClickedEvent, ExcelStyle } from 'ag-grid-community';
import { defaultShortName, TileColumnType } from '../../../tamalelibs/models/tile-column.model';
import { DEFAULT_PAGE_SIZE } from '../../../widgets/grid/grid.model';
import { DataList } from '../../../widgets/grid/grid.config';
import { DashboardService } from '../../../pages/dashboard/dashboard.service';
import { QueryService } from '../../../services/query.service';
import { DateHelperWebService } from '../../../tamalelibs/services/date-helper.web.service';
import { TamDashboardTileType, TamDashboardEvents, TileIcons, TamDashboardActions } from '../tam-dashboard.model';
import { TamDashboardDrillDownConfig } from './tam-dashboard-drill-down.model';
import { TamDashboardTileBaseComponent } from '../tam-dashboard-tile.base.component';
import { TamDashboardPieChartTileComponent } from '../tam-dashboard-pie-chart-tile/tam-dashboard-pie-chart-tile.component';
import { TamDashboardColumnChartTileComponent } from '../tam-dashboard-column-chart-tile/tam-dashboard-column-chart-tile.component';
import { TamDashboardLineChartTileComponent } from '../tam-dashboard-line-chart-tile/tam-dashboard-line-chart-tile.component';
import { TamDashboardValueTileComponent } from '../tam-dashboard-value-tile/tam-dashboard-value-tile.component';
import { TamDashboardMetadataTileComponent } from '../tam-dashboard-metadata-tile/tam-dashboard-metadata-tile.component';
import { TamDashboardGridTileComponent } from '../tam-dashboard-grid-tile/tam-dashboard-grid-tile.component';
import { DynamicComponentService } from '../../../services/dynamic-component.service';
import { TamDashboardTileContainerDirective } from '../tam-dashboard-tile-container/tam-dashboard-tile-container.directive';
import { TamDashboardColumnChartTileConfiguration } from '../tam-dashboard-column-chart-tile/tam-dashboard-column-chart-tile.config';
import { TamDashboardLineChartTileConfiguration } from '../tam-dashboard-line-chart-tile/tam-dashboard-line-chart-tile.config';
import { TamDashboardValueTileConfiguration } from '../tam-dashboard-value-tile/tam-dashboard-value-tile.config';
import { TamDashboardPieChartTileConfiguration } from '../tam-dashboard-pie-chart-tile/tam-dashboard-pie-chart-tile.config';
import { TamDashboardPopUpService } from '../tam-dashboard-popup.service';
import { DashboardTileService } from '../dashboard-tile.service';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { getFocusStateSelector } from '../../../redux/reducers/dashboard.reducer';
import { MultipleFilesRendererComponent } from '../../../widgets/ag-grid/multiple-files-renderer.component';

@Component({
    selector: 'tam-dashboard-drill-down',
    templateUrl: './tam-dashboard-drill-down.component.html',
    styleUrls: ['./tam-dashboard-drill-down.component.scss']
})
export class TamDashboardDrillDownComponent implements OnInit, OnDestroy, OnChanges {

    @Input() config: TamDashboardDrillDownConfig;
    @ViewChild(TamDashboardTileContainerDirective, { static: false }) drilldownContainer: TamDashboardTileContainerDirective;
    gridOptions: GridOptions;
    columnDefs: any = [];
    excelStyles: ExcelStyle[];
    frameworkComponents: any;
    noData = false;
    reloading = false;
    total = 0;
    gridData;

    private _component: TamDashboardTileBaseComponent = null;
    private _destroySubscriptions: Array<Subscription> = [];
    private _firstLoad = true;
    private _gridSort = [{ colId: defaultShortName.guid, sort: 'asc' }];
    private _resetFilter = false;
    private _gridFilter;
    private _gridApi;
    private _gridColumnApi;
    private _notRefreshGridList = false;
    private _actionSubject$ = new Subject();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _dashboardService: DashboardService,
        private _dashboardTileService: DashboardTileService,
        private _queryService: QueryService,
        private _dynamicComponentService: DynamicComponentService,
        private _dashboardPopUpService: TamDashboardPopUpService,
        private _storeQuerier?: StoreQuerierService,
    ) { }

    ngOnInit() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.config.previousValue && !changes.config.previousValue.show && changes.config.currentValue.show) {
            this.columnDefs = [];
            this._resetFilter = false;
            this._notRefreshGridList = false;
            this._gridFilter = null;
            this._initData();
        }
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.closed || item.unsubscribe());
        this._component = null;
    }

    closeClick() {
        this.config.show = false;
        this._firstLoad = true;
        this._gridSort = [{ colId: defaultShortName.guid, sort: 'asc' }];
        this._destroySubscriptions.forEach(item => item.closed || item.unsubscribe());
        this._component = null;
    }

    exportToExcel() {
        const storeFocusValue = this._storeQuerier.queryBySelector(getFocusStateSelector);
        let fileName = this.config.tileConfig.tileName;
        if (storeFocusValue && storeFocusValue.shortName) {
            fileName = fileName + '_' + storeFocusValue.shortName;
        }
        this._dashboardService.exportToXLS(this.gridOptions.columnApi, this.config.tileConfig.columns, this._gridApi, 'sheet1', fileName);
    }

    refreshClick() {
        if (this.config.tileConfig && this.config.tileConfig.visualization.settings && this.config.tileConfig.visualization.settings.categoryAxis) {
            const category = this.config.tileConfig.visualization.settings.categoryAxis;
            const categoryColumn = this.config.tileConfig.columns.filter(item => item.guid === category);
            let categoryDataType;
            if (categoryColumn && categoryColumn.length > 0) {
                categoryDataType = categoryColumn[0].dataType;
            }
            const order = this._dashboardService.isDateType(categoryDataType) ? 'desc' : 'asc';
            this._gridSort = [{ colId: category, sort: order }];
        }
        this.reloading = true;
        this._firstLoad = false;
        this._notRefreshGridList = false;
        // this._firstLoad = true; // refresh as reinit
        this.config.feedbackSubject$.next({
            type: TamDashboardEvents.ITEM_ICON_CLICK,
            payload: {
                id: this.config.id,
                icon: TileIcons.REFRESH.icon
            }
        });
    }

    onGridReady(params) {
        this._gridApi = params.api;
        this._gridColumnApi = params.columnApi;
    }

    private _autoSizeAll(skipHeader = true) {
        const allColumnIds = [];
        this.gridOptions.columnApi.getColumns().forEach(col => {
            allColumnIds.push(col.getColId());
        });
        this.gridOptions.columnApi.autoSizeColumns(allColumnIds, skipHeader);
    }

    private _setGridLayout() {
        if (this._gridApi) {
            this._gridApi.forEachNode((node) => {
                node.setSelected(node.rowIndex === 0);
            });
            this._autoSizeAll(false);
            // need to remove the defaultState sort
            this._gridColumnApi.applyColumnState({ state: this._gridSort, defaultState: { sort: null } });
        }
    }

    private _initData() {
        this.gridOptions = {
            defaultColDef: {
                minWidth: 50,
                resizable: true,
                sortable: true
            },
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            rowSelection: 'single',
            paginationPageSize: DEFAULT_PAGE_SIZE,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
            overlayNoRowsTemplate: '<span style=\'font-size: 20px;background-color:white;color:black\'>No Data Found</span>',
            getContextMenuItems: (param: any) => {
                return this.config.readonly ?
                    this._dashboardService.initMenu(param, this.config.id, this.config.feedbackSubject$, this._actionSubject$, this.config.tileConfig, this._gridFilter) : [];
            },
            onCellClicked: (event: CellClickedEvent) => {
                if (event.column && event.data) {
                    const colId = event.column.getColId();
                    const cellAttribute = event.data[colId + '_attribute'];
                    if (cellAttribute && cellAttribute.threadId) {
                        event.event['slideSheet'] = true;
                        this._dashboardService.openThreadDetailInSlideSheet(cellAttribute.threadId);
                    }
                }
            }
        };
        this.frameworkComponents = {
            cellRenderer: MultipleFilesRendererComponent,
        };
        const styles = this._dashboardService.formatExcelStyle();
        styles.push({
            id: 'font_size10',
            font: {
                size: 10,
                fontName: 'Arial',
            }
        });
        this.excelStyles = styles;
        this._setFocusedEntityIds();
        this._initGridLayout();
        this._destroySubscriptions.push(
            this.config.feedbackSubject$.subscribe(action => this._onSubscribeFeedback(action)),
            this.config.actionSubject$.pipe(
                filter((action: any) => action && action.id === this.config.id),
            ).subscribe(action => this._onSubscribeActions(action)),
            this._actionSubject$.subscribe((data) => {
                this._actionSubjectHandler(data);
            })
        );
        this.reloading = true;
        this._getGridList();
    }

    private _actionSubjectHandler(action) {
        if (action.type === TamDashboardActions.UPDATE_GRID_ROW_DATA) {
            this._updateGridRowItems(action.payload);
        }
    }

    private _updateGridRowItems(data) {
        const gridRowNodes = [];
        this._gridApi.forEachLeafNode((rowNode) => {
            gridRowNodes.push(rowNode);
        });
        this._dashboardTileService.updateGridRowItems(gridRowNodes, data).subscribe((result) => {
            this._gridApi.updateRowData(result);
            setTimeout(() => {
                const model = this._gridApi.getModel();
                this.total = this._dashboardTileService.setGridTotalCount(model);
            }, 500);
            this._notRefreshGridList = true;
            this.config.feedbackSubject$.next({
                type: TamDashboardEvents.ITEM_ICON_CLICK,
                payload: {
                    id: this.config.id,
                    icon: TileIcons.REFRESH.icon
                }
            });
        });
    }

    private _setFocusedEntityIds() {
        this.config.actionSubject$.next({
            id: this.config.id,
            type: TamDashboardActions.SET_FOCUSED_ENTITY_IDS,
            payload: this.config.focusedEntityIds
        });
    }

    private _getGridList(resetFilter?: boolean) {
        const formData = {
            minuteoffset: DateHelperWebService.getContryTimezoneId(),
            showtimestamp: true, // column date server return original value
            showmultipleentityid: true
        };
        if (this.config.focusedEntityIds) {
            formData['focus'] = this.config.focusedEntityIds;
        }
        const chartType = parseInt(this.config.type, 10);
        if (!resetFilter && !this._resetFilter) {
            if (chartType === TamDashboardTileType.PIE_CHART ||
                chartType === TamDashboardTileType.COLUMN_CHART ||
                chartType === TamDashboardTileType.BAR_CHART) {
                formData['filters'] = [{
                    column: this.config.tileConfig.visualization.settings.categoryAxis,
                    value: this.config.category,
                    operator: this.config.category ? 'equals' : 'isempty'
                }];
            } else if (chartType === TamDashboardTileType.LINE_CHART) { // for line chart date axix
                formData['filters'] = [{
                    column: this.config.tileConfig.visualization.settings.groupBy,
                    value: this.config.value,
                    operator: this.config.value ? 'equals' : 'isempty'
                }, {
                    column: this.config.tileConfig.visualization.settings.categoryAxis,
                    value: this.config.category,
                    operator: 'contains'
                }];
            }
            // Value tile does not have filters
            if (formData['filters'] && formData['filters'].length > 0) {
                formData['filters'].forEach((item) => {
                    item.value = this._formatPrivateColumnFilterValue(this.config.tileConfig.columns, item.column, item.value);
                });
            }
            // add a new parameter unseparate for pie chart when the parameter enableUnnestColumn = true
            if (chartType === TamDashboardTileType.PIE_CHART) {
                if (this.config.tileConfig?.visualization?.settings?.enableUnnestColumn) {
                    formData['unseparate'] = true;
                }
            }
        }
        this._gridFilter = formData['filters'];

        this._queryService.sendQueryRequestByChart(this.config.tileConfig, {}, formData).pipe(
            take(1),
            catchError(() => {
                this._notRefreshGridList = false;
                this.reloading = false;
                this._changeDetectorRef.detectChanges();
                return observableOf();
            })).subscribe((res) => {
                if (this.drilldownContainer) {
                    const data = new DataList();
                    data.data = res['Items'];
                    this.total = data.data.length;
                    this.reloading = false;
                    this.gridData = data.data;
                    this.config.show = true;
                    this._notRefreshGridList = false;
                    this._initTemplate();
                    setTimeout(() => {
                        this._setGridLayout();
                    });
                    this._changeDetectorRef.detectChanges();
                }
            });
    }

    private _initGridLayout() {
        if (this.config.tileConfig && this.config.tileConfig.columns && this.config.tileConfig.columns.length > 0) {
            this._dashboardService.formatGridColumns({ tile: this.config.tileConfig }).forEach(col => {
                this.columnDefs.push(col);
            });
        }
    }

    private _onSubscribeFeedback(event) {
        if (event.type === TamDashboardEvents.DRILL_DOWN_SERIES_CLICK) {
            this.config = Object.assign(this.config, event.payload);
            this.reloading = true;
            this._resetFilter = false;
            this._gridSort = [{ colId: defaultShortName.guid, sort: 'asc' }];
            this._getGridList();
        }
    }

    private _onSubscribeActions(action) {
        switch (action.type) {
            case TamDashboardActions.SET_LOADING_STATUS: {
                if (!this._notRefreshGridList) {
                    this.reloading = action.payload;
                }
                break;
            }
            case TamDashboardActions.SET_TILE_DATA: {
                // value chart, when no data, show N/A
                if (this.config.type !== TamDashboardTileType.VALUE.toString()) {
                    if (action.payload) {
                        if (action.payload.length === 0 || (action.payload.data && action.payload.data.length === 0)) {
                            this.noData = true;
                        } else {
                            this.noData = false;
                        }
                    } else {
                        this.noData = true;
                    }
                } else {
                    this.noData = false;
                }
                this.reloading = false;
                break;
            }
            case TamDashboardActions.SET_FOCUSED_ENTITY_IDS: {
                this.config.focusedEntityIds = action.payload;
                if (!this._firstLoad) {
                    this._resetFilter = true;
                    if (!this._notRefreshGridList) {
                        this.reloading = true;
                        this._getGridList(true);
                    }
                }
                break;
            }
        }
    }

    private _initTemplate() {
        let component: any = null;
        if (!this._component) {
            switch (parseInt(this.config.type, 10)) {
                case TamDashboardTileType.PIE_CHART:
                    component = this._dynamicComponentService.loadComponent<TamDashboardPieChartTileComponent>
                        (this.drilldownContainer.viewContainerRef, TamDashboardPieChartTileComponent);
                    break;
                case TamDashboardTileType.COLUMN_CHART:
                case TamDashboardTileType.BAR_CHART:
                    component = this._dynamicComponentService.loadComponent<TamDashboardColumnChartTileComponent>
                        (this.drilldownContainer.viewContainerRef, TamDashboardColumnChartTileComponent,
                            parseInt(this.config.type, 10) === TamDashboardTileType.BAR_CHART);
                    break;
                case TamDashboardTileType.LINE_CHART:
                    component = this._dynamicComponentService.loadComponent<TamDashboardLineChartTileComponent>
                        (this.drilldownContainer.viewContainerRef, TamDashboardLineChartTileComponent);
                    break;
                case TamDashboardTileType.VALUE:
                    component = this._dynamicComponentService.loadComponent<TamDashboardValueTileComponent>
                        (this.drilldownContainer.viewContainerRef, TamDashboardValueTileComponent);
                    break;
                case TamDashboardTileType.METADATA:
                    component = this._dynamicComponentService.loadComponent<TamDashboardMetadataTileComponent>
                        (this.drilldownContainer.viewContainerRef, TamDashboardMetadataTileComponent);
                    break;
                case TamDashboardTileType.GRID:
                    component = this._dynamicComponentService.loadComponent<TamDashboardGridTileComponent>
                        (this.drilldownContainer.viewContainerRef, TamDashboardGridTileComponent);
                    break;
            }
            this._component = component.instance;
        }
        if (this._firstLoad) {
            const id = this.config.id; // '[DrillDown]' +
            switch (parseInt(this.config.type, 10)) {
                case TamDashboardTileType.PIE_CHART: {
                    const config = new TamDashboardPieChartTileConfiguration(this._dashboardTileService, this._dashboardService);
                    config.seriesClickEventType = TamDashboardEvents.DRILL_DOWN_SERIES_CLICK;
                    config.init(id, this.config.readonly, this.config.name,
                        this.config.actionSubject$, this.config.feedbackSubject$);
                    this._setPieChartLabelDispaly(id, this.config.tileConfig.visualization.settings.displaySetting);
                    this._setChartLabelSetting(id);
                    this._component.setConfig(config);
                    config.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.SET_TILE_CONFIG,
                        payload: this.config.tileConfig
                    });
                    // new TamDashboardPieChartTileConfiguration, config.data = undefined
                    config.data = this.config.data;
                    config.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.SET_TILE_DATA,
                        payload: this.config.data
                    });
                    break;

                }
                case TamDashboardTileType.COLUMN_CHART:
                case TamDashboardTileType.BAR_CHART: {
                    const config = new TamDashboardColumnChartTileConfiguration(this._dashboardTileService, this._dashboardService,
                        null, this._dashboardPopUpService,
                        parseInt(this.config.type, 10) === TamDashboardTileType.BAR_CHART);
                    config.seriesClickEventType = TamDashboardEvents.DRILL_DOWN_SERIES_CLICK;
                    config.init(id, this.config.readonly, this.config.name,
                        this.config.actionSubject$, this.config.feedbackSubject$);
                    this._setChartLabelSetting(id);
                    this._component.setConfig(config);
                    config.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.SET_TILE_CONFIG,
                        payload: this.config.tileConfig
                    });
                    config.data = this.config.data;
                    config.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.SET_TILE_DATA,
                        payload: this.config.data
                    });
                    break;
                }
                case TamDashboardTileType.LINE_CHART: {
                    const config = new TamDashboardLineChartTileConfiguration(this._dashboardTileService, this._dashboardService);
                    config.seriesClickEventType = TamDashboardEvents.DRILL_DOWN_SERIES_CLICK;
                    config.init(id, this.config.readonly, this.config.name, this.config.actionSubject$, this.config.feedbackSubject$);
                    this._setChartLabelSetting(id);
                    this._component.setConfig(config);
                    config.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.SET_TILE_CONFIG,
                        payload: this.config.tileConfig
                    });
                    config.data = this.config.data;
                    config.chartToolTipFormat = this.config['chartToolTipFormat'];
                    config.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.SET_TILE_DATA,
                        payload: this.config.data
                    });
                    break;
                }
                case TamDashboardTileType.VALUE: {
                    const config = new TamDashboardValueTileConfiguration(this._dashboardTileService);
                    config.seriesClickEventType = TamDashboardEvents.DRILL_DOWN_SERIES_CLICK;
                    config.init(id, this.config.readonly, this.config.name, this.config.actionSubject$, this.config.feedbackSubject$);
                    this._component.setConfig(config);
                    config.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.SET_TILE_CONFIG,
                        payload: this.config.tileConfig
                    });
                    config.data = this.config.data;
                    config.actionSubject$.next({
                        id: id,
                        type: TamDashboardActions.SET_TILE_DATA,
                        payload: {
                            data: this.config.data,
                            fontSizeCalculator: this._dashboardTileService.getFontSizeCalculator(this.config.data.valueText)
                        }
                    });
                    break;
                }
            }
        }
        this._firstLoad = false;
    }

    private _setPieChartLabelDispaly(id: string, displaySetting: string) {
        this.config.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_PIR_CHART_DISPLAY,
            payload: displaySetting,
        });
    }

    private _setChartLabelSetting(id) {
        const series = this.config.tileConfig.visualization.settings.series;
        const column = this.config.tileConfig.columns.filter(item => item.guid === series);
        if (column && column.length > 0) {
            const dataType = column[0].dataType;
            if (['number', 'currency', 'percent', 'ratio'].indexOf(dataType) !== -1) {
                this.config.actionSubject$.next({
                    id: id,
                    type: TamDashboardActions.SET_CHART_LABEL_SETTING,
                    payload: {
                        dataType: dataType,
                        settings: column[0].settings
                    },
                });
            }
        }
    }

    private _formatPrivateColumnFilterValue(columns, columnId, value) {
        const column = columns.find(item => item.guid === columnId);
        const isPrivateCompany = column && column.columnType === TileColumnType.ENTITY_COLUMN && column.field === 'entity__company';
        if (isPrivateCompany && value === '******') {
            return 'NO_ACCESS';
        }
        return value;
    }
}
